import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Label, Input, Spinner } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { fetchEvaluationDocuments } from './registrationApi';
import { EVALUATION_TYPE } from './registrationConstants';
import DocumentSectionTable from './DocumentSectionTable';
import AddDocumentModal from './AddDocumentModal';

// Tab 2 — Evaluation. Lists the Evaluation Document master data; each row is an
// expandable section holding its documents (rule §Tab Evaluation). Adds stage
// the file locally via the shell's onAddDocument (no BE call); the batch upload
// (POST Draft/Documents) is flushed on Next. Deletes go through onDeleteDocument
// (local for un-flushed rows, DELETE Draft/Documents/{id} for saved ones).
const EvaluationTab = ({
  form,
  setForm,
  documentsByEval,
  onAddDocument,
  onDeleteDocument,
  onNext,
  onBack,
  savingDoc,
  flushing,
  readOnly,
  onDownload,
}) => {
  const [evalDocs, setEvalDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeEvalId, setActiveEvalId] = useState(null);

  const loadEvalDocs = useCallback(async () => {
    setLoading(true);
    try {
      setEvalDocs(await fetchEvaluationDocuments(EVALUATION_TYPE.EVALUATION));
    } catch (error) {
      toast.error(`Failed to load evaluation documents: ${error}`);
      setEvalDocs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvalDocs();
  }, [loadEvalDocs]);

  const openAdd = evaluationId => {
    setActiveEvalId(evaluationId);
    setModalOpen(true);
  };

  const handleSaveDocument = payload => {
    // Local staging only — the file uploads on Next, not here.
    onAddDocument({ ...payload, evaluationId: activeEvalId });
    setModalOpen(false);
  };

  // Expanded content: the per-section document table.
  const ExpandedSection = ({ data }) => (
    <DocumentSectionTable
      documents={documentsByEval[data.id] || []}
      disabled={savingDoc || flushing}
      readOnly={readOnly}
      onAdd={() => openAdd(data.id)}
      onDelete={row => onDeleteDocument(data.id, row)}
      onDownload={onDownload}
    />
  );

  const columns = [
    {
      name: (
        <span className='text-uppercase fw-semibold'>Evaluation Document</span>
      ),
      selector: row => row.evaluasiName,
      wrap: true,
    },
  ];

  return (
    <div>
      {loading ? (
        <div className='text-center py-4'>
          <Spinner size='sm' /> Loading...
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={evalDocs}
          expandableRows
          expandableRowsComponent={ExpandedSection}
          dense
          striped
          noDataComponent={
            <div className='py-3 text-muted'>No evaluation documents found</div>
          }
        />
      )}

      <Row className='mt-3'>
        <Col lg={12}>
          <Label className='form-label'>Notes</Label>
          <Input
            type='textarea'
            rows={3}
            disabled={readOnly}
            value={form.evaluationNote || ''}
            onChange={e =>
              setForm(prev => ({ ...prev, evaluationNote: e.target.value }))
            }
          />
        </Col>
      </Row>

      {!readOnly && (
        <div className='d-flex justify-content-between mt-4'>
          <button
            type='button'
            className='btn btn-light btn-label'
            onClick={onBack}
          >
            <i className='ri-arrow-left-line label-icon align-middle fs-16 me-2'></i>
            Prev
          </button>
          <button
            type='button'
            className='btn btn-primary btn-label right nexttab'
            disabled={flushing}
            onClick={onNext}
          >
            <i className='ri-arrow-right-line label-icon align-middle fs-16 ms-2'></i>
            {flushing ? 'Uploading...' : 'Next'}
          </button>
        </div>
      )}

      <AddDocumentModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        onSave={handleSaveDocument}
      />
    </div>
  );
};

EvaluationTab.propTypes = {
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired,
  documentsByEval: PropTypes.object.isRequired,
  onAddDocument: PropTypes.func.isRequired,
  onDeleteDocument: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  savingDoc: PropTypes.bool,
  flushing: PropTypes.bool,
  readOnly: PropTypes.bool,
  onDownload: PropTypes.func,
};

export default EvaluationTab;
